import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { datePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { XYChartDashoardItemFactoryOptions } from '../common/items/xy-chart';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaRealtimeStatsComponent } from '../mopa-realtime-stats/mopa-realtime-stats.component';
import { RealtimeCloudSaveStatsDataLoaderFactory } from './data-loaders/realtime-cloud-save-stats';
import { RealtimeCloudSaveStatsRateDataLoaderFactory } from './data-loaders/realtime-cloud-save-stats-rate';
import { MopaRealtimeCloudsaveStatsChartDocs } from './mopa-realtime-cloudsave-stats.chart-documentation';
import {
    BiVizRealtimeCloudSaveCommitsDatasource,
    BiVizRealtimeCloudSaveConflictsDatasource,
    BiVizRealtimeCloudSaveDownloadsDatasource,
    BiVizRealtimeCloudSaveIncompleteSavesDatasource,
    BiVizRealtimeCloudSaveLoginsDatasource,
} from './sql-maps/biviz-realtime-cloudsave-generic-sql-map';
import { BiVizRealtimeCloudSaveOthersDatasource } from './sql-maps/biviz-realtime-cloudsave-others-sql-map';

@Component({
    selector: 'app-mopa-realtime-cloudsave-stats',
    templateUrl: './mopa-realtime-cloudsave-stats.component.html',
    styleUrls: ['./mopa-realtime-cloudsave-stats.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaRealtimeCloudSaveStatsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Realtime CloudSave Stats';
    override DASHBOARD_ID = 'mopa-realtime-cloudsave-stats';

    values = Object.values;

    private defaultGcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.realtime_cloudsave_stats.filter.activity_hour':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.activity_hour',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_HOUR',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.activity_weekday':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.activity_weekday',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_WEEKDAY',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_WEEKDAY'),
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.activity_app_version':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.app_version',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION'
                ),
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.firebase_app_store':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.realtime_cloudsave_stats.filter.firebase_app_store',
                    gcpProjectId: this.defaultGcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_APP_STORE',
                    datasourceSqlMap:
                        BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                })
            ).create(),
        'mopa.realtime_cloudsave_stats.filter.firebase_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_tier',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                // filterContext: ['FIREBASE_COUNTRY'],
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.firebase_country':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.firebase_country',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                filterContext: ['FIREBASE_COUNTRY_TIER'],
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.us_region': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_cloudsave_stats.filter.us_region',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
            datasourceSqlMap: BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
        }).create(),
        'mopa.realtime_cloudsave_stats.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_european_union',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        'mopa.realtime_cloudsave_stats.filter.event_project_id':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.event_project_id',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'EVENT_PROJECT_ID',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        // realtime_battery_save
        'mopa.realtime_cloudsave_stats.filter.realtime_battery_save':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_battery_save',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_BATTERY_SAVE',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        // realtime_is_anonymous
        'mopa.realtime_cloudsave_stats.filter.realtime_is_anonymous':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_anonymous',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_IS_ANONYMOUS',
                datasourceSqlMap: [
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveLoginsDatasource.sqlMap,
                ],
            }).create(),
        // realtime_is_small
        'mopa.realtime_cloudsave_stats.filter.realtime_is_small':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_small',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_IS_SMALL',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        // realtime_is_background
        'mopa.realtime_cloudsave_stats.filter.realtime_is_background':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_background',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_IS_BACKGROUND',
                datasourceSqlMap:
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
            }).create(),
        // realtime_error
        'mopa.realtime_cloudsave_stats.filter.realtime_error':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_error',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_ERROR',
                datasourceSqlMap: [
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveConflictsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveDownloadsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveIncompleteSavesDatasource.sqlMap,
                    BiVizRealtimeCloudSaveLoginsDatasource.sqlMap,
                ],
            }).create(),
        // realtime failure rate definition
        'mopa.realtime_cloudsave_stats.filter.realtime_failure_rate_definition':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_failure_rate_definition',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
                datasourceSqlMap: [
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveConflictsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveDownloadsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveIncompleteSavesDatasource.sqlMap,
                    BiVizRealtimeCloudSaveLoginsDatasource.sqlMap,
                ],
            }).create(),
        // realtime_internal_error
        'mopa.realtime_cloudsave_stats.filter.realtime_internal_error':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_internal_error',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_INTERNAL_ERROR',
                datasourceSqlMap: [
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveLoginsDatasource.sqlMap,
                ],
            }).create(),
        // realtime_is_success
        'mopa.realtime_cloudsave_stats.filter.realtime_is_success':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_success',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_IS_SUCCESS',
                datasourceSqlMap: [
                    BiVizRealtimeCloudSaveCommitsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveConflictsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveDownloadsDatasource.sqlMap,
                    BiVizRealtimeCloudSaveIncompleteSavesDatasource.sqlMap,
                    BiVizRealtimeCloudSaveLoginsDatasource.sqlMap,
                ],
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.realtime_cloudsave_stats.modify_time': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId:
                'mopa.realtime_cloudsave_stats.modify_time_realtime_stats_datasource',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiVizRealtimeCloudSaveCommitsDatasource.sqlMap.dataset,
            tableId: BiVizRealtimeCloudSaveCommitsDatasource.sqlMap.tableId,
        }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveCommitsDatasource,
                statsMetrics: [
                    { alias: 'commits_cnt', sql: 'COUNT(*)' },
                    {
                        alias: 'commits_users',
                        sql: 'COUNT(DISTINCT user_id)',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits_rates':
            new RealtimeCloudSaveStatsRateDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits_rates',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveCommitsDatasource,
                specificDatasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveConflictsDatasource,
                statsMetrics: [
                    { sql: 'COUNT(*)', alias: 'conflicts_cnt' },
                    {
                        sql: 'COUNT(DISTINCT user_id)',
                        alias: 'conflicts_users',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts_rates':
            new RealtimeCloudSaveStatsRateDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts_rates',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveConflictsDatasource,
                specificDatasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
            }).create(),

        // downloads
        'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveDownloadsDatasource,
                statsMetrics: [
                    { sql: 'COUNT(*)', alias: 'downloads_cnt' },
                    {
                        sql: 'COUNT(DISTINCT user_id)',
                        alias: 'downloads_users',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads_rates':
            new RealtimeCloudSaveStatsRateDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads_rates',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveDownloadsDatasource,
                specificDatasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
            }).create(),

        // incomplete_saves
        'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource:
                    BiVizRealtimeCloudSaveIncompleteSavesDatasource,
                statsMetrics: [
                    { sql: 'COUNT(*)', alias: 'incomplete_saves_cnt' },
                    {
                        sql: 'COUNT(DISTINCT user_id)',
                        alias: 'incomplete_saves_users',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves_rates':
            new RealtimeCloudSaveStatsRateDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves_rates',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource:
                    BiVizRealtimeCloudSaveIncompleteSavesDatasource,
                specificDatasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveLoginsDatasource,
                statsMetrics: [
                    { sql: 'COUNT(*)', alias: 'logins_cnt' },
                    {
                        sql: 'COUNT(DISTINCT install_id)',
                        alias: 'logins_installs',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins_rates':
            new RealtimeCloudSaveStatsRateDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins_rates',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveLoginsDatasource,
                specificDatasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
                distinctMetric: {
                    col: 'install_id',
                    alias: 'installs',
                },
            }).create(),

        'mopa.realtime_cloudsave_stats.realtime_cloudsave_others':
            new RealtimeCloudSaveStatsDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_others',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                metricsDatasource: BiVizRealtimeCloudSaveOthersDatasource,
                statsMetrics: [
                    {
                        sql: 'SUM(CloudSaveDoubleSessionDetected_count)',
                        alias: 'CloudSaveDoubleSessionDetected_count',
                    },
                    {
                        sql: 'SUM(CloudSaveDoubleSessionDetected_dst_user_count)',
                        alias: 'CloudSaveDoubleSessionDetected_dst_user_count',
                    },
                    {
                        sql: 'SUM(CloudSavePurgeIncompleteStorage_sum)',
                        alias: 'CloudSavePurgeIncompleteStorage_sum',
                    },
                    {
                        sql: 'SUM(IncompleteSaveDialog_count)',
                        alias: 'IncompleteSaveDialog_count',
                    },
                    {
                        sql: 'SUM(IncompleteSaveDialog_dst_user_count)',
                        alias: 'IncompleteSaveDialog_dst_user_count',
                    },
                ],
                ignoredDsColumns: ['REALTIME_FAILURE_RATE_DEFINITION'],
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    settingItemDatetimeAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATETIME_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'hour' as datePartDateTrunc,
            selectOptions: [
                'hour',
                'day',
                'week',
                'month',
                'quarter',
                'year',
            ] as 'hour' | datePartDateTrunc[],
        },
    };

    settingsItemCloudsaveNormalizeMetrics: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS,
            viewtype: 'toggle',
            displayName: 'Normalize metrics',
            value: 'none',
            selectOptions: ['none', 'dau', 'non_anonymous'],
        },
    };

    settingRealtimeHoursUpToLatest: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_HOURS_UP_TO_LATEST,
            viewtype: 'checkbox',
            displayName: 'Filter up to latest hour',
            value: this.getInitialContext().settings.realtimeHoursUpToLatest,
        },
    };

    settingRealtimeCurrentWeekday: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_CURRENT_WEEKDAY,
            viewtype: 'checkbox',
            displayName: 'Filter current weekday',
            value: false,
        },
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATETIME: {
            id: 'mopa.realtime_cloudsave_stats.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATETIME',
                splitable: false,
                rangePresets: [
                    'lifetime',
                    'today',
                    'yesterday',
                    'last 7 days',
                    'last 14 days',
                    'last 30 days',
                    'last 60 days',
                    'last 90 days',
                    'last 180 days',
                    'last 365 days',
                    'custom',
                ],
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_WEEKDAY: {
            id: 'mopa.realtime_cloudsave_stats.filter.activity_weekday',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.activity_weekday',
                    'ACTIVITY_WEEKDAY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.activity_weekday'
                ].emitter,
        },
        ACTIVITY_HOUR: {
            id: 'mopa.realtime_cloudsave_stats.filter.activity_hour',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.activity_hour',
                    'ACTIVITY_HOUR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.activity_hour'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.realtime_cloudsave_stats.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.activity_app_version'
                ].emitter,
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.realtime_cloudsave_stats.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.realtime_cloudsave_stats.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.realtime_cloudsave_stats.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.realtime_cloudsave_stats.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.firebase_country'
                ].emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.realtime_cloudsave_stats.filter.us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.us_region'
                ].emitter,
        },
        EVENT_PROJECT_ID: {
            id: 'mopa.realtime_cloudsave_stats.filter.event_project_id',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.event_project_id',
                    'EVENT_PROJECT_ID'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.event_project_id'
                ].emitter,
        },
        REALTIME_BATTERY_SAVE: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_battery_save',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_battery_save',
                    'REALTIME_BATTERY_SAVE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_battery_save'
                ].emitter,
        },
        REALTIME_IS_ANONYMOUS: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_is_anonymous',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_anonymous',
                    'REALTIME_IS_ANONYMOUS'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_anonymous'
                ].emitter,
        },
        REALTIME_IS_SMALL: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_is_small',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_small',
                    'REALTIME_IS_SMALL'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_small'
                ].emitter,
        },
        REALTIME_IS_BACKGROUND: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_is_background',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_background',
                    'REALTIME_IS_BACKGROUND'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_background'
                ].emitter,
        },
        REALTIME_ERROR: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_error',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_error',
                    'REALTIME_ERROR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_error'
                ].emitter,
        },
        REALTIME_INTERNAL_ERROR: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_internal_error',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_internal_error',
                    'REALTIME_INTERNAL_ERROR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_internal_error'
                ].emitter,
        },
        REALTIME_IS_SUCCESS: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_is_success',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_success',
                    'REALTIME_IS_SUCCESS'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_is_success'
                ].emitter,
        },
        REALTIME_FAILURE_RATE_DEFINITION: {
            id: 'mopa.realtime_cloudsave_stats.filter.realtime_failure_rate_definition',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.filter.realtime_failure_rate_definition',
                datasourceColumnId: 'REALTIME_FAILURE_RATE_DEFINITION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_cloudsave_stats.filter.realtime_failure_rate_definition'
                ].emitter,
        },
    };

    // CHARTS HELPERS

    chartOptionsDashboardItemHelper = (
        options: Pick<
            XYChartDashoardItemFactoryOptions,
            | 'createdInstanceId'
            | 'dashboardItem'
            | 'yColumnLabel'
            | 'metricName'
            | 'yAxisLabel'
            | 'tooltip'
        > & {
            /** Formatter used when normalizing metrics is enabled */
            normalizedMetricTooltipFormatter?: (value: number) => string;
        }
    ): XYChartDashoardItemFactoryOptions => {
        // cannot define options.tooltip and options.normalizedMetricTooltipFormatter at the same time
        if (options.tooltip && options.normalizedMetricTooltipFormatter) {
            throw new Error(
                'Cannot define both options.tooltip and options.normalizedMetricTooltipFormatter'
            );
        }
        return {
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: options.createdInstanceId,
            dashboardItem: options.dashboardItem,
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            xAxisCategoryDatetimeFilterGetter:
                MopaRealtimeStatsComponent.xAxisCategoryDatetimeCurrentWeekdayFilterGetter,
            xColumnLabel: 'datetime',
            yColumnLabel: options.yColumnLabel,
            metricName: options.metricName,
            echartsSeriesType: 'bar',

            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: options.yAxisLabel ?? {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: options.tooltip ?? {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                            new Date(label),
                            context
                        );
                },
                valueFormatterGetter(context) {
                    // if normalizing metrics, use tooltipNumberValueFormatter, else use tooltipIntegerValueFormatter
                    return context.settings['cloudsave.normalizeMetrics']
                        ? (options.normalizedMetricTooltipFormatter ??
                              EChartsTooltipHelper.tooltipNumberValueFormatter)
                        : EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
                splitNameTruncateLimitGetter(context) {
                    return 120;
                },
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
        };
    };

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.realtime_cloudsave_stats.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_cloudsave_stats.modify_time']
                .emitter,
    };

    // commits_cnt
    commits_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.commits_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.commits_cnt',
                dashboardItem: {
                    title: 'Commits count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.commits_cnt,
                },
                yColumnLabel: 'commits_cnt',
                metricName: 'Commits count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits'
            ].emitter,
    };

    // commits_failure_rate
    commits_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.commits_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.commits_failure_rate',
                dashboardItem: {
                    title: 'Commits failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.commits_failure_rate,
                },
                yColumnLabel: 'failure_rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Commits failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits_rates'
            ].emitter,
    };

    // commits_users
    commits_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.commits_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.commits_users',
                dashboardItem: {
                    title: 'Commits users',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.commits_users,
                },
                yColumnLabel: 'commits_users',
                metricName: 'Commits users',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits'
            ].emitter,
    };

    // commits users failure rate
    commits_users_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.commits_users_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.commits_users_failure_rate',
                dashboardItem: {
                    title: 'Commits users failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.commits_users_failure_rate,
                },
                yColumnLabel: 'failure_rate_users',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Commits users failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_commits_rates'
            ].emitter,
    };

    // logins_cnt
    logins_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.logins_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.logins_cnt',
                dashboardItem: {
                    title: 'Logins count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.logins_cnt,
                },
                yColumnLabel: 'logins_cnt',
                metricName: 'Logins count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins'
            ].emitter,
    };

    // logins_failure_rate
    logins_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.logins_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.logins_failure_rate',
                dashboardItem: {
                    title: 'Logins failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.logins_failure_rate,
                },
                yColumnLabel: 'failure_rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Logins failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins_rates'
            ].emitter,
    };

    // logins_installs
    logins_installs: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.logins_installs',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.logins_installs',
                dashboardItem: {
                    title: 'Logins installs',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.logins_installs,
                },
                yColumnLabel: 'logins_installs',
                metricName: 'Logins installs',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins'
            ].emitter,
    };

    // logins installs failure rate
    logins_installs_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.logins_installs_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.logins_installs_failure_rate',
                dashboardItem: {
                    title: 'Logins installs failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.logins_installs_failure_rate,
                },
                yColumnLabel: 'failure_rate_installs',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Logins installs failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_logins_rates'
            ].emitter,
    };

    // downloads_cnt
    downloads_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.downloads_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.downloads_cnt',
                dashboardItem: {
                    title: 'Downloads count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.downloads_cnt,
                },
                yColumnLabel: 'downloads_cnt',
                metricName: 'Downloads count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads'
            ].emitter,
    };
    // downloads_failure_rate
    downloads_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.downloads_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.downloads_failure_rate',
                dashboardItem: {
                    title: 'Downloads failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.downloads_failure_rate,
                },
                yColumnLabel: 'failure_rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Downloads failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads_rates'
            ].emitter,
    };

    // downloads_users
    downloads_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.downloads_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.downloads_users',
                dashboardItem: {
                    title: 'Downloads users',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.downloads_users,
                },
                yColumnLabel: 'downloads_users',
                metricName: 'Downloads users',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads'
            ].emitter,
    };

    // downloads_users_failure_rate
    downloads_users_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.downloads_users_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.downloads_users_failure_rate',
                dashboardItem: {
                    title: 'Downloads users failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.downloads_users_failure_rate,
                },
                yColumnLabel: 'failure_rate_users',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Downloads users failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_downloads_rates'
            ].emitter,
    };

    // conflicts_cnt
    conflicts_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.conflicts_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.conflicts_cnt',
                dashboardItem: {
                    title: 'Conflicts count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.conflicts_cnt,
                },
                yColumnLabel: 'conflicts_cnt',
                metricName: 'Conflicts count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts'
            ].emitter,
    };

    // conflicts_failure_rate
    conflicts_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.conflicts_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.conflicts_failure_rate',
                dashboardItem: {
                    title: 'Conflicts failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.conflicts_failure_rate,
                },
                yColumnLabel: 'failure_rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Conflicts failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts_rates'
            ].emitter,
    };

    // conflicts_users
    conflicts_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.conflicts_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.conflicts_users',
                dashboardItem: {
                    title: 'Conflicts users',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.conflicts_users,
                },
                yColumnLabel: 'conflicts_users',
                metricName: 'Conflicts users',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts'
            ].emitter,
    };

    // conflicts users failure rate
    conflicts_users_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_cloudsave_stats.chart.conflicts_users_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_cloudsave_stats.chart.conflicts_users_failure_rate',
                dashboardItem: {
                    title: 'Conflicts users failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.conflicts_users_failure_rate,
                },
                yColumnLabel: 'failure_rate_users',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Conflicts users failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_conflicts_rates'
            ].emitter,
    };

    // incomplete_saves_cnt
    incomplete_saves_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_saves_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_saves_cnt',
                dashboardItem: {
                    title: 'Incomplete Saves count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_saves_cnt,
                },
                yColumnLabel: 'incomplete_saves_cnt',
                metricName: 'Incomplete Saves count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves'
            ].emitter,
    };

    // incomplete_saves_failure_rate
    incomplete_saves_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_saves_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_saves_failure_rate',
                dashboardItem: {
                    title: 'Incomplete Saves failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_saves_failure_rate,
                },
                yColumnLabel: 'failure_rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Incomplete Saves failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves_rates'
            ].emitter,
    };
    // incomplete_saves_users
    incomplete_saves_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_saves_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_saves_users',
                dashboardItem: {
                    title: 'Incomplete Saves users',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_saves_users,
                },
                yColumnLabel: 'incomplete_saves_users',
                metricName: 'Incomplete Saves users',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves'
            ].emitter,
    };
    // incomplete_saves_users_failure_rate
    incomplete_saves_users_failure_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_saves_users_failure_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_saves_users_failure_rate',
                dashboardItem: {
                    title: 'Incomplete Saves users failure rate',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_saves_users_failure_rate,
                },
                yColumnLabel: 'failure_rate_users',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                metricName: 'Incomplete Saves users failure rate',
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                    splitNameTruncateLimitGetter(context) {
                        return 120;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_incomplete_saves_rates'
            ].emitter,
    };

    // CloudSaveDoubleSessionDetected_count
    cloud_save_double_session_detected_count: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_double_session_detected_count',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_double_session_detected_count',
                dashboardItem: {
                    title: 'Double Session Detected count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.cloud_save_double_session_detected_count,
                },
                yColumnLabel: 'CloudSaveDoubleSessionDetected_count',
                metricName: 'Double Session Detected count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_others'
            ].emitter,
    };
    // CloudSaveDoubleSessionDetected_dst_user_count
    cloud_save_double_session_detected_dst_user_count: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.realtime_stats.chart.cloud_save_double_session_detected_dst_user_count',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ](
                this.chartOptionsDashboardItemHelper({
                    createdInstanceId:
                        'mopa.realtime_stats.chart.cloud_save_double_session_detected_dst_user_count',
                    dashboardItem: {
                        title: 'Double Session Detected users',
                        chartDocumentation:
                            MopaRealtimeCloudsaveStatsChartDocs.cloud_save_double_session_detected_dst_user_count,
                    },
                    yColumnLabel:
                        'CloudSaveDoubleSessionDetected_dst_user_count',
                    metricName: 'Double Session Detected users',
                })
            ),
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.realtime_cloudsave_stats.realtime_cloudsave_others'
                ].emitter,
        };
    // CloudSavePurgeIncompleteStorage_sum
    cloud_save_purge_incomplete_storage_sum: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_purge_incomplete_storage_sum',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_purge_incomplete_storage_sum',
                dashboardItem: {
                    title: 'Purge Incomplete Storage sum',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.cloud_save_purge_incomplete_storage_sum,
                },
                yColumnLabel: 'CloudSavePurgeIncompleteStorage_sum',
                metricName: 'Purge Incomplete Storage sum',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_others'
            ].emitter,
    };
    // IncompleteSaveDialog_count
    incomplete_save_dialog_count: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_save_dialog_count',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_save_dialog_count',
                dashboardItem: {
                    title: 'Incomplete Save Dialog count',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_save_dialog_count,
                },
                yColumnLabel: 'IncompleteSaveDialog_count',
                metricName: 'Incomplete Save Dialog count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_others'
            ].emitter,
    };
    // IncompleteSaveDialog_dst_user_count
    incomplete_save_dialog_dst_user_count: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.incomplete_save_dialog_dst_user_count',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.incomplete_save_dialog_dst_user_count',
                dashboardItem: {
                    title: 'Incomplete Save Dialog users',
                    chartDocumentation:
                        MopaRealtimeCloudsaveStatsChartDocs.incomplete_save_dialog_dst_user_count,
                },
                yColumnLabel: 'IncompleteSaveDialog_dst_user_count',
                metricName: 'Incomplete Save Dialog users',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_cloudsave_stats.realtime_cloudsave_others'
            ].emitter,
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaUsersComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaUsersComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const failureRateDefinitionIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'REALTIME_FAILURE_RATE_DEFINITION',
                state: {
                    value: {
                        options: ['failure'],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [failureRateDefinitionIC];

        // default date aggregation
        initialContext.settings['datetimeAggregation'] = 'day';

        // initialContext.settings['realtimeHoursUpToLatest'] = true;
        // initialContext.settings['realtimeCurrentWeekday'] = true;
        initialContext.settings[
            QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS
        ] = 'none';

        return initialContext;
    }
}
